"use client";

import Link from "next/link";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

/*
 * Opens the composer from anywhere in the editorial shell: Post in the
 * phone bar, Add + top right on desktop. The composer itself is mounted
 * once by the frame (`EditorialComposer`), as an overlay, so taking part
 * never changes the page.
 */

export type ComposerKind = "post" | "story" | "playlist";
export type ComposerTool = "link" | "poll" | "music" | null;

export interface ComposerOpenOptions {
	kind?: ComposerKind;
	tool?: ComposerTool;
	/** Open on the story Drafts list. */
	drafts?: boolean;
	/** Open on Music with this track (a submission) already picked. */
	trackId?: number;
}

export interface ComposerToast {
	message: string;
	href?: string;
	linkLabel?: string;
	/** A button instead of a link, e.g. reopen the composer on Drafts. */
	action?: { label: string; onClick: () => void };
}

interface ComposerState {
	open: boolean;
	options: ComposerOpenOptions;
	/** Bumped on each open, so the composer starts fresh. */
	session: number;
	openComposer: (options?: ComposerOpenOptions) => void;
	closeComposer: () => void;
	showToast: (toast: ComposerToast) => void;
}

const ComposerContext = createContext<ComposerState | null>(null);

export function useComposer() {
	return useContext(ComposerContext);
}

export function ComposerProvider({ children }: { children: ReactNode }) {
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<ComposerOpenOptions>({});
	const [session, setSession] = useState(0);
	const [toast, setToast] = useState<ComposerToast | null>(null);
	const returnFocus = useRef<HTMLElement | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const openComposer = useCallback((next: ComposerOpenOptions = {}) => {
		returnFocus.current =
			document.activeElement instanceof HTMLElement
				? document.activeElement
				: null;
		setOptions(next);
		setSession((value) => value + 1);
		setOpen(true);
	}, []);

	const closeComposer = useCallback(() => {
		setOpen(false);
		const target = returnFocus.current;
		requestAnimationFrame(() => target?.focus?.());
	}, []);

	const showToast = useCallback((next: ComposerToast) => {
		setToast(next);
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => setToast(null), 5000);
	}, []);

	useEffect(
		() => () => {
			if (timer.current) clearTimeout(timer.current);
		},
		[],
	);

	const value = useMemo(
		() => ({
			open,
			options,
			session,
			openComposer,
			closeComposer,
			showToast,
		}),
		[open, options, session, openComposer, closeComposer, showToast],
	);

	return (
		<ComposerContext.Provider value={value}>
			{children}
			{toast ? (
				<output className="ed-cmp-toast ed-ui">
					<span>{toast.message}</span>
					{toast.action ? (
						<button
							type="button"
							className="ed-button-text ed-ui ed-link"
							onClick={() => {
								setToast(null);
								toast.action?.onClick();
							}}
						>
							{toast.action.label}
						</button>
					) : toast.href ? (
						<Link
							href={toast.href}
							className="ed-link"
							onClick={() => setToast(null)}
						>
							{toast.linkLabel ?? "View"}
						</Link>
					) : null}
				</output>
			) : null}
		</ComposerContext.Provider>
	);
}

/** Desktop top right: Add +, for members. */
export function ComposerAddButton() {
	const composer = useComposer();
	if (!composer) return null;
	return (
		<button
			type="button"
			className="ed-add-button"
			aria-haspopup="dialog"
			aria-expanded={composer.open}
			onClick={() => composer.openComposer()}
		>
			Add <span aria-hidden>+</span>
		</button>
	);
}
